import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getData, deleteData, postData } from "../../services/FetchNodeservices";
import JobForm from "./JobForm";
import AddCandidateForm from "./AddCandidateForm";
import toast, { Toaster } from "react-hot-toast";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { motion } from "framer-motion";
import dayjs from "dayjs";

import {
  FaUserPlus,
  FaTable,
  FaUsers,
  FaTrash,
  FaEdit,
  FaEllipsisV,
  FaFilter,
  FaBuilding,
} from "react-icons/fa";

import { MdSearch } from "react-icons/md";


// ================= MODAL =================
function Modal({ open, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-3xl animate-fadeIn">
        <div className="bg-white rounded-[32px] shadow-2xl overflow-hidden border border-gray-100 p-8">
          {children}
        </div>
      </div>
    </div>
  );
}


// ================= TOOLBAR =================
const Toolbar = ({ search, onSearchChange, onNewClick, onAddVendorClick, filters, onFilterChange, onResetFilters, jobs }) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);

  // Compute unique values from the loaded jobs array for dropdown options
  const uniqueReqIds = [...new Set(jobs.map((j) => j.reqId).filter(Boolean))].sort();
  const uniqueTitles = [...new Set(jobs.map((j) => j.title).filter(Boolean))].sort();
  const uniqueCompanies = [...new Set(jobs.map((j) => j.company).filter(Boolean))].sort();
  const uniqueStatuses = [...new Set(jobs.map((j) => j.status).filter(Boolean))].sort();
  const uniqueLocations = [...new Set(jobs.map((j) => j.location).filter(Boolean))].sort();
  const uniqueAssignedBy = [...new Set(jobs.map((j) => j.assignedBy).filter(Boolean))].sort();
  const uniqueAssignedTo = [...new Set(
    jobs.flatMap((j) => (j.assignedTo ? j.assignedTo.split(", ") : [])).filter(Boolean)
  )].sort();

  return (
    <div className="space-[#f4f6fa] space-y-4">
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        
        {/* SEARCH & TOGGLE FILTER */}
        <div className="flex-1 w-full flex gap-2">
          <div className="flex-1 relative">
            <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search jobs by title, company..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border transition duration-200 text-xs font-extrabold ${
              showFilters 
                ? "bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm" 
                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaFilter className="w-3.5 h-3.5" /> Filters
          </button>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex gap-2 w-full md:w-auto">
          <button
            onClick={onNewClick}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold shadow-lg shadow-indigo-100 transition duration-200 text-xs"
          >
            <FaUserPlus /> New Job
          </button>

          <button
            onClick={onAddVendorClick}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold shadow-lg shadow-emerald-100 transition duration-200 text-xs"
          >
            <FaBuilding /> Add Vendor
          </button>

          <button
            onClick={() => navigate("/dashboard/jobtable")}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-extrabold shadow-sm transition duration-200 text-xs"
          >
            <FaTable /> Table View
          </button>
        </div>
      </div>

      {/* COLLAPSIBLE FILTERS PANEL */}
      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-100 animate-slideDown">
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">Requisition ID</label>
            <select
              value={filters.reqId}
              onChange={(e) => onFilterChange("reqId", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Requisitions</option>
              {uniqueReqIds.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">Job Title</label>
            <select
              value={filters.title}
              onChange={(e) => onFilterChange("title", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Job Titles</option>
              {uniqueTitles.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">Company</label>
            <select
              value={filters.company}
              onChange={(e) => onFilterChange("company", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Companies</option>
              {uniqueCompanies.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">Status</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange("status", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">Location</label>
            <select
              value={filters.location}
              onChange={(e) => onFilterChange("location", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Locations</option>
              {uniqueLocations.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">Assigned By</label>
            <select
              value={filters.assignedBy}
              onChange={(e) => onFilterChange("assignedBy", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Assignees</option>
              {uniqueAssignedBy.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">Assigned To</label>
            <select
              value={filters.assignedTo}
              onChange={(e) => onFilterChange("assignedTo", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Assigned To</option>
              {uniqueAssignedTo.map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">From Date</label>
            <input
              type="date"
              value={filters.fromDate || ""}
              onChange={(e) => onFilterChange("fromDate", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-widest mb-1.5">To Date</label>
            <input
              type="date"
              value={filters.toDate || ""}
              onChange={(e) => onFilterChange("toDate", e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition duration-200 text-xs font-semibold"
            />
          </div>
          <div className="flex items-end justify-end">
            <button
              onClick={onResetFilters}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-700 font-extrabold text-xs transition duration-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// ================= JOB CARD =================
const JobCard = ({ job, onAddCandidate, onEditJob, onDeleteJob, canAssign, onAssignJob }) => {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);

  const statusColor = {
    Active: "bg-green-50 text-green-700 border border-green-100",
    Hold: "bg-amber-50 text-amber-700 border border-amber-100",
    Closed: "bg-red-50 text-red-700 border border-red-100",
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="bg-white rounded-3xl border border-gray-100 p-6 shadow-xl shadow-indigo-100/10 hover:shadow-indigo-100/30 transition duration-200 relative flex flex-col justify-between min-h-[220px]"
    >
      <div>
        {/* HEADER */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              #{job.reqId}
            </span>
            <span
              className={`px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider ${
                statusColor[job.status] || "bg-gray-50 text-gray-500 border border-gray-100"
              }`}
            >
              {job.status}
            </span>
          </div>

          {/* MENU ACTION BUTTON */}
          <div className="relative">
            <button 
              onClick={() => setMenu(!menu)}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-50 transition"
            >
              <FaEllipsisV className="w-3.5 h-3.5" />
            </button>

            {menu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
                <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 overflow-hidden py-1">      
                  <button
                    onClick={() => {
                      setMenu(false);
                      onAddCandidate(job);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition"
                  >
                    ➕ Add Candidate
                  </button>
                  <button
                    onClick={() => {
                      setMenu(false);
                      onEditJob(job);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition"
                  >
                    ✏️ Edit Job
                  </button>

                  {canAssign && (
                    <button
                      onClick={() => {
                        setMenu(false);
                        onAssignJob(job);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition border-t border-gray-50"
                    >
                      👤 Assign to Recruiter
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setMenu(false);
                      const link = `${window.location.origin}/career/job/${job.reqId}`;
                      navigator.clipboard.writeText(link);
                      toast.success("Job link copied successfully!");
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition border-t border-gray-50"
                  >
                    🔗 Generate Link
                  </button>
                  <button
                    onClick={() => {
                      setMenu(false);
                      onDeleteJob(job);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-red-50 text-red-650 text-xs font-bold transition border-t border-gray-50"
                  >
                    🗑 Delete Job
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* TITLE */}
        <h2
          onClick={() => navigate(`/career/job/${job.reqId}`)}
          className="text-base font-black text-indigo-950 cursor-pointer hover:text-indigo-600 transition"
        >
          {job.title}
        </h2>

        {/* COMPANY */}
        <p className="text-xs font-semibold text-gray-400 mt-1">{job.company}</p>

        {/* INFO */}
        <div className="flex gap-4 text-xs font-semibold text-gray-500 mt-3">
          <span className="flex items-center gap-1">📍 {job.location || "-"}</span>
          <span className="flex items-center gap-1">📅 {job.created_at ? dayjs(job.created_at).format("YYYY-MM-DD") : "-"}</span>
        </div>
      </div>

      <div>
        {/* CANDIDATES */}
        <div
          onClick={() => navigate(`/jobcandidates/${job.reqId}`)}
          className="mt-4 bg-[#f4f6fa]/60 hover:bg-[#f4f6fa] border border-gray-100 p-3 rounded-2xl cursor-pointer flex justify-between items-center transition"
        >
          <span className="text-xs text-indigo-650 font-bold flex items-center gap-1.5">
            👥 {job.candidates || 0} Candidates
          </span>
          <span className="text-xs text-indigo-600 font-extrabold">View →</span>
        </div>

        {/* FOOTER */}
        <div className="text-[10px] text-gray-400 font-semibold mt-3 pt-3 border-t border-gray-50 flex justify-between items-center">
          <div>Posted: {job.postedBy || "-"}</div>
          <div>Assigned: {job.assignedTo || "-"}</div>
        </div>
      </div>
    </motion.div>
  );
};


// ================= MAIN =================
export default function FeatureJobs() {
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState(() => {
    return sessionStorage.getItem("featureJobs_search") || "";
  });

  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem("featureJobs_filters");
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (e) {
        console.error("Error parsing saved filters", e);
      }
    }
    return {
      reqId: "",
      title: "",
      company: "",
      status: "",
      location: "",
      assignedBy: "",
      assignedTo: "",
      fromDate: "",
      toDate: ""
    };
  });

  const [scrollRestored, setScrollRestored] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCandidateFormOpen, setIsCandidateFormOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Vendor / Company Form State
  const [isVendorFormOpen, setIsVendorFormOpen] = useState(false);
  const initialVendorForm = {
    company_name: "",
    company_website: "",
    describe_company: "",
    vertical: "",
    contact_person: "",
    mobile_number: "",
    email_id: "",
    personal_email: "",
    designation: "",
    location: "",
    address: "",
    gst_number: "",
    linkedin_profile: "",
    remark: "",
    status: "Active",
    agreement_from: "",
    agreement_to: "",
    percentage: "",
  };
  const [vendorForm, setVendorForm] = useState(initialVendorForm);
  const [vendorLoading, setVendorLoading] = useState(false);

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!vendorForm.company_name.trim()) {
      toast.error("Company Name is required!");
      return;
    }
    setVendorLoading(true);
    try {
      const res = await postData("vendor/add", vendorForm);
      if (res?.status) {
        toast.success("Vendor / Company added successfully!");
        setIsVendorFormOpen(false);
        setVendorForm(initialVendorForm);
        fetchJobs();
      } else {
        toast.error(res?.message || "Failed to add vendor");
      }
    } catch (err) {
      console.error("Vendor add error:", err);
      toast.error("An error occurred while adding vendor");
    } finally {
      setVendorLoading(false);
    }
  };

  const userId = Number(localStorage.getItem("user_id") || 0);
  const [canAssign, setCanAssign] = useState(false);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [assignForm, setAssignForm] = useState({
    assigned_to: [],
    notes: "",
    target_count: "",
    assign_date: new Date().toISOString().split("T")[0],
  });

  // FETCH EMPLOYEES (Assign - Hierarchy Based)
  useEffect(() => {
    async function loadEmployees() {
      try {
        if (!userId) return;
        const result = await getData("job/allowed-users/" + userId);
        if (result.status) {
          setEmployees(result.data || []);
          setCanAssign(result.canAssign || false);
        }
      } catch (err) {
        console.error(err);
      }
    }
    loadEmployees();
  }, [userId]);

  const handleAssignJob = (job) => {
    setSelectedJob(job);
    
    // Parse currently assigned recruiters' names from job.assignedTo
    const currentlyAssigned = job.assignedTo
      ? job.assignedTo.split(", ").map(name => name.trim()).filter(Boolean)
      : [];

    setAssignForm({
      assigned_to: currentlyAssigned,
      notes: "",
      target_count: "",
      assign_date: new Date().toISOString().split("T")[0],
    });
    setAssignDialogOpen(true);
  };

  const handleAssignSubmit = async () => {
    if (!selectedJob) {
      toast.error("No job selected");
      return;
    }

    if (assignForm.assigned_to.length === 0) {
      toast.error("Select at least one employee");
      return;
    }

    if (!assignForm.target_count || String(assignForm.target_count).trim() === "") {
      toast.error("Please add target count");
      return;
    }

    try {
      const selectedEmployeeIds = assignForm.assigned_to
        .map((empName) => {
          const emp = employees.find((e) => e.name === empName);
          return emp ? (emp.user_id || emp.id) : null;
        })
        .filter(Boolean);

      const body = {
        req_id: selectedJob.reqId,
        assigned_to: selectedEmployeeIds.join(","),
        assigned_by: userId,
        notes: assignForm.notes,
        target_count: assignForm.target_count,
        assign_date: assignForm.assign_date,
      };

      const res = await postData("job/assign", body);

      if (res.status) {
        toast.success("Job assigned successfully!");
        setAssignDialogOpen(false);
        setSelectedJob(null);

        setAssignForm({
          assigned_to: [],
          notes: "",
          target_count: "",
          assign_date: new Date().toISOString().split("T")[0],
        });

        fetchJobs();
      } else {
        toast.error(res.message || "Assignment failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred during assignment");
    }
  };

  // FETCH JOBS
  const fetchJobs = async () => {
    const result = await getData("job/jobs");
    if (result.status) {
      setJobs(result.jobs || result.data || []);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // Save search to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem("featureJobs_search", search);
  }, [search]);

  // Save filters to sessionStorage when they change
  useEffect(() => {
    sessionStorage.setItem("featureJobs_filters", JSON.stringify(filters));
  }, [filters]);

  // Save scroll position to sessionStorage
  useEffect(() => {
    const mainEl = document.querySelector("main");
    const handleScroll = () => {
      const scrollTop = Math.max(
        mainEl ? mainEl.scrollTop : 0,
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );
      sessionStorage.setItem("featureJobs_scrollPos", scrollTop);
    };

    if (mainEl) {
      mainEl.addEventListener("scroll", handleScroll);
    }
    window.addEventListener("scroll", handleScroll);

    return () => {
      if (mainEl) {
        mainEl.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Restore scroll position after jobs are fetched and rendered
  useEffect(() => {
    if (jobs.length > 0 && !scrollRestored) {
      const savedScroll = sessionStorage.getItem("featureJobs_scrollPos");
      if (savedScroll) {
        const scrollTopVal = parseInt(savedScroll, 10);
        const scrollContainer = document.querySelector("main");
        
        const restore = () => {
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollTopVal;
          }
          window.scrollTo(0, scrollTopVal);
          document.documentElement.scrollTop = scrollTopVal;
          document.body.scrollTop = scrollTopVal;
        };

        // Restore scroll position multiple times to accommodate dynamic image or styling loading times
        const timer1 = setTimeout(restore, 50);
        const timer2 = setTimeout(restore, 150);
        const timer3 = setTimeout(restore, 300);
        const timer4 = setTimeout(restore, 600);

        setScrollRestored(true);

        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
          clearTimeout(timer4);
        };
      } else {
        setScrollRestored(true);
      }
    }
  }, [jobs, scrollRestored]);

  // ACTIONS
  const handleAddCandidate = (job) => {
    setSelectedJob(job);
    setIsCandidateFormOpen(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setIsFormOpen(true);
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm("Are you sure you want to delete this job opening?")) return;

    const result = await deleteData(`job/delete-job/${job.reqId}`);
    if (result.status) {
      toast.success("Job deleted successfully");
      fetchJobs();
    } else {
      toast.error(result.message || "Delete failed");
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      reqId: "",
      title: "",
      company: "",
      status: "",
      location: "",
      assignedBy: "",
      assignedTo: "",
      fromDate: "",
      toDate: ""
    });
  };

  // FILTER
  const filteredJobs = jobs.filter((job) => {
    // 1. General search search query
    const matchesSearch = [job.title, job.company, job.reqId]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase());

    if (!matchesSearch) return false;

    // 2. Specific filters
    if (filters.reqId && !job.reqId?.toLowerCase().includes(filters.reqId.toLowerCase())) return false;
    if (filters.title && !job.title?.toLowerCase().includes(filters.title.toLowerCase())) return false;
    if (filters.company && !job.company?.toLowerCase().includes(filters.company.toLowerCase())) return false;
    if (filters.status && job.status !== filters.status) return false;
    if (filters.location && !job.location?.toLowerCase().includes(filters.location.toLowerCase())) return false;
    if (filters.assignedBy && !job.assignedBy?.toLowerCase().includes(filters.assignedBy.toLowerCase())) return false;
    if (filters.assignedTo && !job.assignedTo?.toLowerCase().includes(filters.assignedTo.toLowerCase())) return false;

    // Date filters
    if (filters.fromDate) {
      if (!job.created_at) return false;
      const jobDateStr = dayjs(job.created_at).format("YYYY-MM-DD");
      if (jobDateStr < filters.fromDate) return false;
    }
    if (filters.toDate) {
      if (!job.created_at) return false;
      const jobDateStr = dayjs(job.created_at).format("YYYY-MM-DD");
      if (jobDateStr > filters.toDate) return false;
    }

    return true;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans">
        <Toaster position="top-right" />

        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            Job Openings
          </h1>
          <p className="text-sm font-semibold text-gray-400 mt-1">
            Manage, edit, publish new listings and sync direct candidate submissions
          </p>
        </div>

        {/* TOOLBAR */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-2xl shadow-indigo-100/20">
          <Toolbar
            search={search}
            onSearchChange={setSearch}
            onNewClick={() => {
              setSelectedJob(null);
              setIsFormOpen(true);
            }}
            onAddVendorClick={() => setIsVendorFormOpen(true)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onResetFilters={handleResetFilters}
            jobs={jobs}
          />
        </div>

        {/* GRID */}
        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredJobs.map((job, i) => (
              <JobCard
                key={i}
                job={job}
                onAddCandidate={handleAddCandidate}
                onEditJob={handleEditJob}
                onDeleteJob={handleDeleteJob}
                canAssign={canAssign}
                onAssignJob={handleAssignJob}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-3xl p-16 text-center text-gray-400 font-semibold shadow-md">
            <FaUsers size={40} className="mx-auto mb-4 text-indigo-300" />
            <p>No job openings found matching search criteria.</p>
          </div>
        )}

        {/* JOB FORM */}
        {isFormOpen && (
          <Modal open={isFormOpen} onClose={() => setIsFormOpen(false)}>
            <div className="w-full">
              <JobForm
                job={selectedJob}
                onClose={() => {
                  setIsFormOpen(false);
                  fetchJobs();
                }}
              />
            </div>
          </Modal>
        )}

        {/* CANDIDATE FORM */}
        {isCandidateFormOpen && selectedJob && (
          <Modal open={isCandidateFormOpen} onClose={() => setIsCandidateFormOpen(false)}>
            <div className="w-full">
              <AddCandidateForm
                job={selectedJob}
                onClose={() => {
                  setIsCandidateFormOpen(false);
                  fetchJobs();
                }}
              />
            </div>
          </Modal>
        )}

        {/* ASSIGN MODAL */}
        {assignDialogOpen && (
          <Modal open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
            <div className="w-full">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-black text-gray-900">Assign Job</h3>
                  {selectedJob && <p className="text-[10px] text-gray-400 mt-0.5">{selectedJob.title}</p>}
                </div>
                <button 
                  onClick={() => setAssignDialogOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Employee List */}
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Assign to Employees</label>

                  <div className="border border-gray-150 bg-[#f4f6fa]/30 rounded-2xl p-3 max-h-48 overflow-y-auto space-y-1.5 no-scrollbar">
                    {employees.map((emp) => (
                      <label key={emp.id} className="flex items-center gap-2.5 py-1 text-xs font-semibold text-gray-700 cursor-pointer hover:text-gray-900">
                        <input
                          type="checkbox"
                          className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                          checked={assignForm.assigned_to.includes(emp.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAssignForm((prev) => ({
                                ...prev,
                                assigned_to: [...prev.assigned_to, emp.name],
                              }));
                            } else {
                              setAssignForm((prev) => ({
                                ...prev,
                                assigned_to: prev.assigned_to.filter(
                                  (n) => n !== emp.name
                                ),
                              }));
                            }
                          }}
                        />
                        {emp.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Assignment Notes</label>
                  <textarea
                    placeholder="Enter details, requests, or constraints..."
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold h-20"
                    value={assignForm.notes}
                    onChange={(e) =>
                      setAssignForm((p) => ({ ...p, notes: e.target.value }))
                    }
                  />
                </div>

                {/* Target Count & Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Target Count</label>
                    <input
                      type="number"
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                      value={assignForm.target_count}
                      onChange={(e) =>
                        setAssignForm((p) => ({
                          ...p,
                          target_count: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Assign Date</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                      value={assignForm.assign_date}
                      onChange={(e) =>
                        setAssignForm((p) => ({
                          ...p,
                          assign_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    className="bg-[#f4f6fa] text-gray-700 font-bold px-5 py-2.5 rounded-2xl hover:bg-gray-100 transition text-xs"
                    onClick={() => setAssignDialogOpen(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className={`px-5 py-2.5 rounded-2xl font-bold transition text-xs ${
                      assignForm.assigned_to.length === 0
                        ? "bg-gray-150 text-gray-400 cursor-not-allowed bg-gray-100"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-100"
                    }`}
                    disabled={assignForm.assigned_to.length === 0}
                    onClick={handleAssignSubmit}
                  >
                    Assign Job
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {/* VENDOR / COMPANY FORM MODAL */}
        {isVendorFormOpen && (
          <Modal open={isVendorFormOpen} onClose={() => setIsVendorFormOpen(false)}>
            <div className="w-full">
              <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-black text-gray-900">Add New Vendor / Company</h3>
                  <p className="text-xs text-gray-400 font-bold mt-0.5">
                    Add a new vendor or company profile to the vendor directory
                  </p>
                </div>
                <button
                  onClick={() => setIsVendorFormOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleVendorSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2 no-scrollbar">
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Company Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Morgan Job"
                      value={vendorForm.company_name}
                      onChange={(e) => setVendorForm({ ...vendorForm, company_name: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Company Website</label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={vendorForm.company_website}
                      onChange={(e) => setVendorForm({ ...vendorForm, company_website: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Vertical / Industry</label>
                    <input
                      type="text"
                      placeholder="e.g. IT Services, Healthcare"
                      value={vendorForm.vertical}
                      onChange={(e) => setVendorForm({ ...vendorForm, vertical: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Contact Person</label>
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={vendorForm.contact_person}
                      onChange={(e) => setVendorForm({ ...vendorForm, contact_person: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Mobile Number</label>
                    <input
                      type="text"
                      placeholder="+91 9876543210"
                      value={vendorForm.mobile_number}
                      onChange={(e) => setVendorForm({ ...vendorForm, mobile_number: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Official Email ID</label>
                    <input
                      type="email"
                      placeholder="contact@company.com"
                      value={vendorForm.email_id}
                      onChange={(e) => setVendorForm({ ...vendorForm, email_id: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Personal Email</label>
                    <input
                      type="email"
                      placeholder="personal@email.com"
                      value={vendorForm.personal_email}
                      onChange={(e) => setVendorForm({ ...vendorForm, personal_email: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Designation</label>
                    <input
                      type="text"
                      placeholder="HR Manager, SPOC"
                      value={vendorForm.designation}
                      onChange={(e) => setVendorForm({ ...vendorForm, designation: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Location</label>
                    <input
                      type="text"
                      placeholder="e.g. Mumbai, Delhi"
                      value={vendorForm.location}
                      onChange={(e) => setVendorForm({ ...vendorForm, location: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">GST Number</label>
                    <input
                      type="text"
                      placeholder="27AAAAA0000A1Z5"
                      value={vendorForm.gst_number}
                      onChange={(e) => setVendorForm({ ...vendorForm, gst_number: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Agreement From</label>
                    <input
                      type="date"
                      value={vendorForm.agreement_from}
                      onChange={(e) => setVendorForm({ ...vendorForm, agreement_from: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Agreement To</label>
                    <input
                      type="date"
                      value={vendorForm.agreement_to}
                      onChange={(e) => setVendorForm({ ...vendorForm, agreement_to: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Percentage (%)</label>
                    <input
                      type="number"
                      placeholder="e.g. 8.33"
                      step="0.01"
                      value={vendorForm.percentage}
                      onChange={(e) => setVendorForm({ ...vendorForm, percentage: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Status</label>
                    <select
                      value={vendorForm.status}
                      onChange={(e) => setVendorForm({ ...vendorForm, status: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Address</label>
                    <input
                      type="text"
                      placeholder="Full Office Address"
                      value={vendorForm.address}
                      onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Describe Company</label>
                    <textarea
                      placeholder="Brief overview of company operations..."
                      rows={2}
                      value={vendorForm.describe_company}
                      onChange={(e) => setVendorForm({ ...vendorForm, describe_company: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Remark</label>
                    <textarea
                      placeholder="Any internal notes or remarks..."
                      rows={2}
                      value={vendorForm.remark}
                      onChange={(e) => setVendorForm({ ...vendorForm, remark: e.target.value })}
                      className="w-full px-3.5 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-200 focus:outline-none focus:border-indigo-500 text-xs font-semibold"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsVendorFormOpen(false)}
                    className="bg-[#f4f6fa] text-gray-700 font-bold px-5 py-2.5 rounded-2xl hover:bg-gray-100 transition text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={vendorLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2.5 rounded-2xl shadow-lg shadow-emerald-100 transition text-xs"
                  >
                    {vendorLoading ? "Saving..." : "Save Vendor"}
                  </button>
                </div>
              </form>
            </div>
          </Modal>
        )}
      </div>
    </LocalizationProvider>
  );
}
